import { useMemo, useEffect, JSX } from "react";

import { Alert, AlertTitle, Box, Grid, LinearProgress, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { useLocationsDataQuery } from "src/api/locationQueries.api";
import LocationSearch from "src/components/Dashboard/LocationSearch/LocationSearch";
import { groupLocationOptions } from "src/components/Dashboard/LocationSearch/locationSearch.helper";
import TogglePinned from "src/components/Dashboard/TooglePinned/TogglePinned";
import ErrorAlert from "src/components/ErrorAlert/ErrorAlert";
import { getDestinationRouteFromLocationType } from "src/components/Sidebar/sidebar.helpers";
import TopNav from "src/components/TopNav/TopNav";
import Pages from "src/constants/pages.enum";
import { getAvailablePagesBasedOnLocationStructure } from "src/lib/locationTree.helper";
import { getAppRolesFromUser } from "src/lib/user.helper";
import { useAppErrorStore } from "src/store/error.store";
import { LocationModel } from "src/types/location";
import LocationType from "src/types/LocationType";

const styles = {
  fullWidth: { xs: 12 },
};

function GlobalLayout(): JSX.Element {
  const navigate = useNavigate();
  const theme = useTheme();
  const auth = useAuth();
  const { t } = useTranslation();
  const { locationId } = useParams();
  const { error, setError } = useAppErrorStore();
  const { data: locations, isLoading } = useLocationsDataQuery();

  const allLocations = useMemo(() => locations?.value ?? [], [locations]);
  const userAppRoles = useMemo(() => getAppRolesFromUser(auth.user), [auth.user]);
  const availablePages: Pages[] = useMemo(
    () => getAvailablePagesBasedOnLocationStructure(allLocations, userAppRoles),
    [allLocations, userAppRoles]
  );
  const groups = useMemo(() => groupLocationOptions(allLocations), [allLocations]);

  const hasAnyMallChildren = useMemo(() => {
    return groups.filter((g) => g.type === LocationType.Mall).some((group) => group.children.length > 0);
  }, [groups]);

  const hasAnyShopChildren = useMemo(() => {
    return groups.filter((g) => g.type === LocationType.Shop).some((group) => group.children.length > 0);
  }, [groups]);

  const hasAnyStationChildren = useMemo(() => {
    return groups.filter((g) => g.type === LocationType.Station).some((group) => group.children.length > 0);
  }, [groups]);

  const selectedLocation = useMemo(() => allLocations.find((location) => location.Id === locationId), [locationId, allLocations]);

  useEffect(() => {
    if (!allLocations.length || locationId) return;

    let firstLocation;

    if (availablePages.includes(Pages.CenterDashboard)) {
      firstLocation = groups.find((g) => g.type === LocationType.Mall)?.children?.[0];
    }

    if (availablePages.includes(Pages.ShopDashboard)) {
      firstLocation = groups.find((g) => g.type === LocationType.Shop)?.children?.[0];
    }

    if (availablePages.includes(Pages.StationDashboard)) {
      firstLocation = groups.find((g) => g.type === LocationType.Station)?.children?.[0];
    }

    if (firstLocation) {
      navigate(getDestinationRouteFromLocationType(firstLocation?.type, firstLocation.id));
    }
  }, [groups, availablePages, locationId, allLocations, navigate]);

  const handleSelectedLocation = (selectedLocation: LocationModel, locationType: LocationType, isRegion: boolean = false): void => {
    navigate(getDestinationRouteFromLocationType(locationType, selectedLocation.Id, isRegion));
  };

  return (
    <>
      <ErrorAlert error={error} setError={setError} />
      <TopNav
        selectedLocation={
          <Typography variant='h3' textTransform='none'>
            {selectedLocation?.LocalName}
          </Typography>
        }
        togglePinned={
          <TogglePinned
            fontSize={theme.fontSizes.xl}
            locationId={selectedLocation?.Id}
            type={LocationType[selectedLocation?.Type as keyof typeof LocationType]}
            setError={setError}
          />
        }
      >
        <LocationSearch
          selectedLocation={selectedLocation ?? null}
          locationOptions={allLocations}
          handleSelectedLocation={handleSelectedLocation}
        />
      </TopNav>
      <Box>
        <Grid container spacing={2}>
          {isLoading && (
            <Grid size={styles.fullWidth}>
              <LinearProgress />
            </Grid>
          )}
          {!isLoading && groups.length > 0 && !hasAnyMallChildren && !hasAnyShopChildren && !hasAnyStationChildren && (
            <Grid size={styles.fullWidth}>
              <Alert severity='warning'>
                <AlertTitle>{t("Configuration Error")}</AlertTitle>
                {t("It appears that there is an issue with the configuration of your account. Please contact ASE Support for assistance.")}
              </Alert>
            </Grid>
          )}
          <Outlet />
        </Grid>
      </Box>
    </>
  );
}

export default GlobalLayout;
